import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, useFormState } from 'react-final-form';

import {
  errorMessagesActions,
  errorTypes,
  FormFieldAdapter,
  Messages,
  Question,
  questionHelpers,
  QuestionsGroup,
} from '~/components';
import { isNotEmpty } from '~/utils';

import { triggerEditPageChangesMadeAnalytics } from '~/analytics/analytics.listener';
import { YOUR_DETAILS_FIELDS as defaultFields } from './you-details-fields.config';
import { envConfigSelectors } from '~/components/configuration/configuration.selectors';
import { Title } from '~/elements';
import { scrollToFirstErrorElement } from '~/utils';

import styles from './YourDetails.scss';

const errorMessage = `YourDetails:${errorTypes.QUESTION_FIELD_ERROR}`;

export class YourDetails extends PureComponent {
  state = {
    isSectionInvalid: false,
    currentQuestionsNames: [],
    allQuestionsNames: [],
  };

  static getDerivedStateFromProps(
    { formSyncErrors: { yourDetailsSection: errors = {} }, questions },
    { currentQuestionsNames, allQuestionsNames },
  ) {
    if (questions.length > 0 && allQuestionsNames.length === 0) {
      return {
        allQuestionsNames: questionHelpers.getAllQuestionsReduxNames(questions),
        currentQuestionsNames: questionHelpers.getParentQuestionsReduxNames(questions),
      };
    }

    const yourDetailsErrors = Object.keys(errors).filter(
      (questionName) => allQuestionsNames.findIndex((name) => name === questionName) === -1,
    );
    const yourDetailsQuestionsErrors = Object.keys(errors).filter(
      (questionName) => currentQuestionsNames.findIndex((name) => name === questionName) !== -1,
    );

    const isSectionInvalid =
      isNotEmpty(yourDetailsErrors) || isNotEmpty(yourDetailsQuestionsErrors);

    return { isSectionInvalid, currentQuestionsNames };
  }

  componentDidUpdate(prevProps) {
    const {
      errorMessages,
      formSyncErrors: { yourDetailsSection: errors = {} },
      dispatchRemoveSingleErrorMessage,
      dispatchPushSectionError,
      dispatchClearSectionErrors,
    } = this.props;
    const { isSectionInvalid } = this.state;

    // Only scroll if error messages changed
    if (prevProps.errorMessages !== errorMessages) {
      scrollToFirstErrorElement('[class*="containerError"]');
    }

    // Only dispatch actions if the validation state actually changed
    const prevErrors = prevProps.formSyncErrors?.yourDetailsSection || {};
    const errorsChanged = JSON.stringify(prevErrors) !== JSON.stringify(errors);

    if (errorsChanged) {
      dispatchClearSectionErrors('yourDetails');
      if (isSectionInvalid) {
        dispatchPushSectionError(`${errorMessage}:yourDetails`);
      } else {
        dispatchRemoveSingleErrorMessage(errorMessage);
      }
    }
  }

  onChangeHandler = (event, newValue, previousValue, name) => {
    const { editMode, initialFormValues } = this.props;
    if (editMode) {
      const fieldName = name.split('.')[1];
      const fieldInitialValue = initialFormValues[fieldName];
      if (newValue !== fieldInitialValue) {
        triggerEditPageChangesMadeAnalytics('details', 'details');
      }
    }
  };

  addQuestionsNames = (names) => {
    this.setState((prevState) => ({
      currentQuestionsNames: [...new Set(prevState.currentQuestionsNames.concat(names))],
    }));
  };

  removeQuestionsNames = (names) => {
    this.setState((prevState) => ({
      currentQuestionsNames: prevState.currentQuestionsNames.filter(
        (name) => !names.includes(name),
      ),
    }));
  };

  render() {
    const {
      fields = defaultFields,
      questions,
      questionsSectionsConfig,
      editMode,
      remotePhoneNumberConfig,
      YourDetailsConfig,
      fieldNamePrefix = '',
    } = this.props;

    const { title } = YourDetailsConfig;

    return (
      <fieldset data-testid="YourDetails">
        <Title className={styles.title}>{title}</Title>
        <Messages forTypes={[errorMessage]} />
        <div className={styles.formGroup} data-testid="YourDetails">
          {fields.map(({ name, ...fieldConfig }) => {
            const fieldName = `${fieldNamePrefix}${name}`;
            if (name === 'phoneNumber' || name === 'phoneNumberAlternative') {
              return (
                <div key={name} className={styles.phoneNumber}>
                  <Field
                    formFieldConfig={
                      name !== 'phoneNumberAlternative'
                        ? { ...fieldConfig, ...remotePhoneNumberConfig }
                        : { ...fieldConfig }
                    }
                    component={FormFieldAdapter}
                    name={fieldName}
                    onChange={this.onChangeHandler}
                  />
                </div>
              );
            }
            if (name === 'title') {
              return (
                <div key={name} className={styles.nameTitle}>
                  <Field
                    formFieldConfig={{ ...fieldConfig }}
                    component={FormFieldAdapter}
                    name={fieldName}
                    onChange={this.onChangeHandler}
                  />
                </div>
              );
            }

            return (
              <Field
                formFieldConfig={{ ...fieldConfig }}
                component={FormFieldAdapter}
                name={fieldName}
                onChange={this.onChangeHandler}
                key={name}
              />
            );
          })}
          {questionsSectionsConfig &&
            questionsSectionsConfig[0] &&
            questionsSectionsConfig[0].displayQuestionsInYourDetails &&
            questions.length > 0 &&
            questions.map((question) =>
              question.children && question.children.length ? (
                <QuestionsGroup
                  key={question.id}
                  question={question}
                  formSectionName="yourDetailsSection"
                  addQuestionsNames={this.addQuestionsNames}
                  removeQuestionsNames={this.removeQuestionsNames}
                  editMode={editMode}
                />
              ) : (
                <Question
                  key={question.id}
                  editMode={editMode}
                  question={question}
                  formSectionName="yourDetailsSection"
                />
              ),
            )}
        </div>
      </fieldset>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    services: {
      config: { AboutYouForm = {}, questionsSectionsConfig },
    },
    questions: { questions },
    appointment: {
      draft: {
        appointmentType: { id: appointmentTypeId },
      },
    },
    messages: {
      app: { errorMessages },
    },
  } = state;

  return {
    fields: AboutYouForm.yourDetailsFields,
    questions: questions && questions.RETRIEVED_QUESTIONS,
    questionsSectionsConfig,
    errorMessages: errorMessages || [],
    remotePhoneNumberConfig: envConfigSelectors.remotePhoneNumberConfig(appointmentTypeId)(state),
    YourDetailsConfig: envConfigSelectors.selectYourDetailsConfig(state),
  };
};

const mapDispatchToProps = {
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,
  dispatchPushSectionError: errorMessagesActions.pushSectionError,
  dispatchClearSectionErrors: errorMessagesActions.clearSectionErrors,
};

// Wrapper component to get form state from react-final-form
const YourDetailsWithFormState = (props) => {
  const formState = useFormState();
  const formSyncErrors = {
    yourDetailsSection: formState.errors?.yourDetailsSection || {},
  };

  return <YourDetails {...props} formSyncErrors={formSyncErrors} />;
};

export const ConnectedYourDetails = connect(
  mapStateToProps,
  mapDispatchToProps,
)(YourDetailsWithFormState);

YourDetails.propTypes = {
  formSyncErrors: PropTypes.shape({}),
  initialFormValues: PropTypes.shape({}),
  editMode: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  questions: PropTypes.arrayOf(PropTypes.shape({})),
  questionsSectionsConfig: PropTypes.arrayOf(PropTypes.shape({})),
  remotePhoneNumberConfig: PropTypes.shape({}),
  YourDetailsConfig: PropTypes.shape({
    title: PropTypes.string,
  }),
  fieldNamePrefix: PropTypes.string,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
  dispatchPushSectionError: PropTypes.func.isRequired,
  dispatchClearSectionErrors: PropTypes.func.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

YourDetails.defaultProps = {
  editMode: false,
  initialFormValues: {},
  fields: undefined,
  questionsSectionsConfig: null,
  questions: [],
  remotePhoneNumberConfig: {},
  fieldNamePrefix: '',
  formSyncErrors: {},
  YourDetailsConfig: {
    title: 'Your details',
  },
};
